// Two small pieces for settings pages: a confirmation dialog (optionally "type the name to confirm", for things
// that can't be undone) and a "⋯" menu of actions (a popover on desktop, a bottom sheet on phones).
import { useEffect, useRef, useState } from "react";
import { isNarrow } from "../mobile.ts";
import { Sheet } from "./Sheet.tsx";
import "./server-settings.css";

export function Confirm({
  title,
  children,
  confirmLabel,
  danger,
  typeToConfirm,
  onConfirm,
  onClose,
}: {
  title: string;
  children?: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /** The confirm button stays off until this exact text is typed (deleting a channel: its name). */
  typeToConfirm?: string;
  /** Resolves to an error message to show, or null when done (the dialog then closes). */
  onConfirm: () => Promise<string | null>;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = !typeToConfirm || typed.trim() === typeToConfirm;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Esc closes the dialog, not the settings page behind it.
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  const go = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const problem = await onConfirm().catch((e: Error) => e.message);
    setBusy(false);
    if (problem) setError(problem);
    else onClose();
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        style={{ maxWidth: 440 }}
        onSubmit={(e) => {
          e.preventDefault();
          void go();
        }}
      >
        <h2 id="confirm-title">{title}</h2>
        {children && <div className="confirm-body">{children}</div>}
        {typeToConfirm && (
          <div className="field">
            <label htmlFor="confirm-type">
              Type <strong>{typeToConfirm}</strong> to confirm
            </label>
            <input id="confirm-type" className="input" autoFocus autoComplete="off" autoCapitalize="off" spellCheck={false} value={typed} onChange={(e) => setTyped(e.target.value)} />
          </div>
        )}
        {error && <p className="error" role="alert" style={{ margin: 0 }}>{error}</p>}
        <div className="confirm-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className={`btn ${danger ? "btn-danger" : "btn-primary"}`} disabled={!ready || busy} autoFocus={!typeToConfirm}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

export interface MenuAction {
  label: string;
  danger?: boolean;
  onSelect: () => void;
}

/** A "⋯" button with a few actions. */
export function ActionMenu({ label, actions }: { label: string; actions: MenuAction[] }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open || isNarrow()) return;
    const onDown = (e: PointerEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);
  if (!actions.length) return null;
  const pick = (a: MenuAction) => {
    setOpen(false);
    a.onSelect();
  };
  const items = (
    <div className="action-menu" role="menu" aria-label={label}>
      {actions.map((a) => (
        <button key={a.label} type="button" role="menuitem" className={`action-item${a.danger ? " danger" : ""}`} onClick={() => pick(a)}>
          {a.label}
        </button>
      ))}
    </div>
  );
  return (
    <span className="action-wrap" ref={wrap}>
      <button type="button" className="icon-btn" aria-haspopup="menu" aria-expanded={open} aria-label={label} title={label} onClick={() => setOpen((v) => !v)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
      </button>
      {open &&
        (isNarrow() ? (
          <Sheet label={label} onClose={() => setOpen(false)}>
            {items}
          </Sheet>
        ) : (
          <div className="action-popover">{items}</div>
        ))}
    </span>
  );
}
